import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, Package, ChevronLeft, ChevronRight, SlidersHorizontal, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';

import PortalLayout from './PortalLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../../components/ui/sheet';
import { portalCatalogAPI } from '../../lib/portalApi';
import AddToCart from '../../components/portal/AddToCart';
import CatalogReels from '../../components/portal/CatalogReels';
import ProductImage from '../../components/ProductImage';
import PortalProductQuickView from '../../components/portal/PortalProductQuickView';
import RecentlyViewedReel from '../../components/portal/RecentlyViewedReel';

const PAGE_SIZE = 24;

// ---- Stock badge -----------------------------------------------------------
// Green/red pill the user explicitly asked for. We also add a yellow "Limited"
// state so they don't accidentally promise something on the edge of running out.
const StockBadge = ({ status }) => {
    const { t } = useTranslation('portal');
    const cfg = {
        in_stock: { label: t('catalog.stock.in_stock'),  bg: 'bg-emerald-50',  text: 'text-emerald-700',  dot: 'bg-emerald-500' },
        low:      { label: t('catalog.stock.low'),       bg: 'bg-amber-50',    text: 'text-amber-700',    dot: 'bg-amber-500' },
        out:      { label: t('catalog.stock.out'),       bg: 'bg-red-50',      text: 'text-red-700',      dot: 'bg-red-500' },
    }[status] || { label: status, bg: 'bg-gray-50', text: 'text-gray-700', dot: 'bg-gray-400' };
    return (
        <span
            data-testid={`stock-badge-${status}`}
            className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

// ---- Product card (grid) ---------------------------------------------------
// Clickable card — opens the Quick-view modal. Add-to-Cart's own click
// handler is scoped with `stopPropagation` so the two never conflict.
// Vertical layout with a generous square image, then a clean info block:
//   1. Brand + Stock badge
//   2. Name (2-line clamp)
//   3. SKU · price (price-row)
//   4. Add-to-cart / qty stepper
const ProductCard = ({ p, onOpen }) => {
    const { t } = useTranslation('portal');
    return (
    <div
        data-testid={`product-card-${p.sku}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(p.sku)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(p.sku); } }}
        className="group bg-white border border-[#EFE5E1] rounded-xl overflow-hidden hover:shadow-lg hover:border-[#C08478]/40 transition-all flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C08478]/40"
    >
        <div className="relative aspect-square bg-gradient-to-br from-[#FBF8F6] to-[#F5EEEA] flex items-center justify-center overflow-hidden">
            <ProductImage
                product={p}
                testid={`product-card-${p.sku}-img`}
                imgClassName="group-hover:scale-[1.06] transition-transform duration-500"
            />
            {/* Stock badge floats over the image — instantly scannable */}
            <div className="absolute top-3 right-3">
                <StockBadge status={p.stock_status} />
            </div>
            {p.brand && (
                <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-[10px] uppercase tracking-[2px] text-[#1A1A1A] font-semibold px-2 py-0.5 rounded-full">
                    {p.brand}
                </div>
            )}
        </div>
        <div className="p-4 flex flex-col gap-3 flex-1">
            <h3 className="text-[14px] font-semibold text-[#1A1A1A] line-clamp-2 min-h-[40px] leading-snug" title={p.name}>
                {p.name}
            </h3>
            <div className="flex items-end justify-between gap-2 mt-auto">
                <span className="text-[10px] text-gray-400 font-mono tracking-wider">{p.sku}</span>
                {p.price_hidden ? (
                    <span className="text-[11px] italic text-[#C08478]">{t('catalog.price_on_request')}</span>
                ) : (
                    <span className="text-[18px] font-bold text-[#1A1A1A] tabular-nums leading-none">
                        €{Number(p.price).toFixed(2)}
                    </span>
                )}
            </div>
            {/* AddToCart interactions must not bubble up to the card's onClick — otherwise
                clicking + inside the stepper would ALSO open the quick-view modal. */}
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <AddToCart product={p} variant="card" />
            </div>
        </div>
    </div>
    );
};

// ---- Skeletons -------------------------------------------------------------
const Skeleton = () => (
    <div className="bg-white border border-[#EFE5E1] rounded-lg overflow-hidden animate-pulse">
        <div className="aspect-square bg-[#F5EEEA]" />
        <div className="p-4 space-y-2">
            <div className="h-3 bg-[#F5EEEA] rounded w-1/3" />
            <div className="h-4 bg-[#F5EEEA] rounded w-5/6" />
            <div className="h-4 bg-[#F5EEEA] rounded w-1/3" />
        </div>
    </div>
);

// ---- Page ------------------------------------------------------------------
const PortalCatalog = () => {
    const { t } = useTranslation('portal');
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingBrands, setLoadingBrands] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);

    // Filters — `brandId` is the canonical source, mirrored to the URL
    // (`?brand=<id>`) so the sidebar links can drive it and the
    // catalog stays linkable / shareable.
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [brandId, setBrandIdState] = useState(searchParams.get('brand') || null);
    const [categoryId, setCategoryId] = useState(null);
    const [sort, setSort] = useState('name_asc');
    const [inStockOnly, setInStockOnly] = useState(false);
    const [page, setPage] = useState(1);

    // Quick-view modal — sku=null when closed. Opening a product card
    // sets the sku; the modal fetches full detail via the new endpoint.
    const [quickviewSku, setQuickviewSku] = useState(null);
    const openQuickview = useCallback((sku) => setQuickviewSku(sku), []);

    // Wrapper that keeps URL in sync with the brand filter. Strips the
    // param when null so the URL stays clean for "All".
    const setBrandId = useCallback((next) => {
        setBrandIdState(next);
        const sp = new URLSearchParams(searchParams);
        if (next) sp.set('brand', next);
        else sp.delete('brand');
        setSearchParams(sp, { replace: true });
        window.dispatchEvent(new Event('uc-portal-brand-change'));
    }, [searchParams, setSearchParams]);

    // React to back/forward and to clicks on the sidebar (which mutate
    // the URL directly via <Link to=…> + dispatch the custom event).
    useEffect(() => {
        const fromUrl = searchParams.get('brand') || null;
        if (fromUrl !== brandId) setBrandIdState(fromUrl);
    }, [searchParams, brandId, location.key]);

    // Debounce the search field so we don't spam the API on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Brands list — fetched once on mount.
    useEffect(() => {
        let mounted = true;
        portalCatalogAPI.brands()
            .then(({ data }) => { if (mounted) setBrands(data); })
            .catch(() => toast.error(t('catalog.loading_brands_failed')))
            .finally(() => { if (mounted) setLoadingBrands(false); });
        return () => { mounted = false; };
    }, [t]);

    // Categories list — fetched once on mount (used by the category filter).
    useEffect(() => {
        let mounted = true;
        portalCatalogAPI.categories()
            .then(({ data }) => { if (mounted) setCategories(Array.isArray(data) ? data : []); })
            .catch(() => { /* category facet is a progressive enhancement */ });
        return () => { mounted = false; };
    }, []);

    // Products list — re-fetched on filter / pagination changes.
    const fetchProducts = useCallback(async () => {
        setLoadingProducts(true);
        try {
            const params = { page, page_size: PAGE_SIZE, sort };
            if (debouncedSearch) params.q = debouncedSearch;
            if (brandId) params.brand_id = brandId;
            if (categoryId) params.category_id = categoryId;
            if (inStockOnly) params.in_stock_only = true;
            const { data } = await portalCatalogAPI.products(params);
            setProducts(data.items);
            setTotal(data.total);
            setHasMore(data.has_more);
        } catch {
            toast.error(t('catalog.loading_products_failed'));
        } finally {
            setLoadingProducts(false);
        }
    }, [page, sort, debouncedSearch, brandId, categoryId, inStockOnly, t]);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    // Reset to page 1 whenever any filter changes — the user always
    // expects the result set to start at the top after a filter change.
    useEffect(() => { setPage(1); }, [debouncedSearch, brandId, categoryId, inStockOnly, sort]);

    const activeBrand = useMemo(
        () => brands.find((b) => b.id === brandId),
        [brands, brandId],
    );
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const clearFilters = () => {
        setSearch('');
        setBrandId(null);
        setCategoryId(null);
        setInStockOnly(false);
        setSort('name_asc');
    };

    return (
        <PortalLayout>
            <div className="space-y-6" data-testid="portal-catalog-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('catalog.section_label')}</p>
                        <h1 className="text-3xl font-bold text-[#1A1A1A]">
                            {activeBrand ? activeBrand.name : t('catalog.title_all')}
                        </h1>
                        <p className="text-sm text-gray-500 mt-1" data-testid="catalog-results-count">
                            {loadingProducts ? t('common.loading') : t(total === 1 ? 'catalog.results_one' : 'catalog.results_other', { count: total })}
                        </p>
                    </div>
                </div>

                <div className="space-y-5" data-testid="catalog-main">
                    {/* New arrivals + Best sellers reels — only shown when no filters active */}
                    {!brandId && !debouncedSearch && !inStockOnly && page === 1 && <CatalogReels onOpen={openQuickview} />}
                    {/* Recently viewed — renders below the reels only when
                        the customer has opened at least one product this
                        session. Cleared automatically on tab close. */}
                    {!brandId && !debouncedSearch && !inStockOnly && page === 1 && <RecentlyViewedReel onOpen={openQuickview} />}

                    {/* Top bar — Filters drawer + search + sort + view toggle */}
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Brand filter drawer (slide-out) */}
                        <Sheet>
                            <SheetTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="catalog-open-filters-btn"
                                    className="bg-white border-[#EFE5E1] gap-1.5 relative"
                                >
                                    <SlidersHorizontal size={14} />
                                    {t('catalog.sidebar_brands')}
                                    {(brandId || inStockOnly) && (
                                        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#C08478]" />
                                    )}
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-[300px] sm:w-[340px] overflow-y-auto" data-testid="catalog-filter-drawer">
                                <SheetHeader>
                                    <SheetTitle>{t('catalog.sidebar_brands')}</SheetTitle>
                                </SheetHeader>
                                <div className="mt-6 space-y-6">
                                    <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1" data-testid="brand-filter-list">
                                        <button
                                            onClick={() => setBrandId(null)}
                                            data-testid="brand-filter-all"
                                            className={`w-full flex items-center justify-between text-sm px-3 py-2 rounded-md transition-colors ${
                                                !brandId
                                                    ? 'bg-[#1A1A1A] text-white'
                                                    : 'text-gray-600 hover:bg-[#F5EEEA] hover:text-[#1A1A1A]'
                                            }`}
                                        >
                                            <span>{t('catalog.all_brands')}</span>
                                            <span className="text-[10px] opacity-70">{brands.reduce((s, b) => s + b.product_count, 0)}</span>
                                        </button>
                                        {loadingBrands ? (
                                            <>
                                                <div className="h-8 bg-[#F5EEEA] rounded animate-pulse" />
                                                <div className="h-8 bg-[#F5EEEA] rounded animate-pulse" />
                                                <div className="h-8 bg-[#F5EEEA] rounded animate-pulse" />
                                            </>
                                        ) : (
                                            brands.map((b) => (
                                                <button
                                                    key={b.id}
                                                    onClick={() => setBrandId(b.id)}
                                                    data-testid={`brand-filter-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                                    className={`w-full flex items-center justify-between text-sm px-3 py-2 rounded-md transition-colors ${
                                                        brandId === b.id
                                                            ? 'bg-[#1A1A1A] text-white'
                                                            : 'text-gray-600 hover:bg-[#F5EEEA] hover:text-[#1A1A1A]'
                                                    }`}
                                                >
                                                    <span className="truncate text-left">{b.name}</span>
                                                    <span className="text-[10px] opacity-70 ml-2">{b.product_count}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-xs uppercase tracking-[2px] text-gray-500 font-semibold mb-3">{t('catalog.sidebar_availability')}</h3>
                                        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={inStockOnly}
                                                onChange={(e) => setInStockOnly(e.target.checked)}
                                                data-testid="catalog-instock-toggle"
                                                className="w-4 h-4 accent-[#1A1A1A]"
                                            />
                                            {t('catalog.in_stock_only')}
                                        </label>
                                    </div>
                                    {(brandId || inStockOnly || debouncedSearch) && (
                                        <Button
                                            onClick={clearFilters}
                                            variant="ghost"
                                            size="sm"
                                            data-testid="catalog-clear-filters-btn"
                                            className="w-full text-[#C08478] hover:text-[#A0695E]"
                                        >
                                            <X size={14} className="mr-1" /> {t('catalog.clear_filters')}
                                        </Button>
                                    )}
                                </div>
                            </SheetContent>
                        </Sheet>

                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('catalog.search_placeholder')}
                                className="pl-10 bg-white border-[#EFE5E1] focus:border-[#C08478]"
                                data-testid="catalog-search-input"
                            />
                        </div>

                        <Select
                            value={categoryId || '__all__'}
                            onValueChange={(v) => setCategoryId(v === '__all__' ? null : v)}
                        >
                            <SelectTrigger
                                className="w-[200px] bg-white border-[#EFE5E1]"
                                data-testid="catalog-category-select"
                            >
                                <SelectValue placeholder={t('catalog.all_categories')} />
                            </SelectTrigger>
                            <SelectContent className="max-h-[60vh]">
                                <SelectItem value="__all__" data-testid="catalog-category-option-all">
                                    {t('catalog.all_categories')}
                                </SelectItem>
                                {categories.map((c) => (
                                    <SelectItem
                                        key={c.id}
                                        value={c.id}
                                        data-testid={`catalog-category-option-${c.id}`}
                                    >
                                        {c.name}{typeof c.product_count === 'number' ? ` (${c.product_count})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger
                                className="w-[180px] bg-white border-[#EFE5E1]"
                                data-testid="catalog-sort-select"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="name_asc">{t('catalog.sort_name_asc')}</SelectItem>
                                <SelectItem value="name_desc">{t('catalog.sort_name_desc')}</SelectItem>
                                <SelectItem value="price_asc">{t('catalog.sort_price_asc')}</SelectItem>
                                <SelectItem value="price_desc">{t('catalog.sort_price_desc')}</SelectItem>
                                <SelectItem value="newest">{t('catalog.sort_newest')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                        {/* Product grid (vertical layout only — never horizontal scroll) */}
                        {loadingProducts ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
                                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={`sk-${i}`} />)}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="bg-white border border-[#EFE5E1] rounded-lg p-12 text-center" data-testid="catalog-empty">
                                <Package size={32} className="mx-auto text-gray-300 mb-3" />
                                <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('catalog.empty_title')}</h3>
                                <p className="text-sm text-gray-500">{t('catalog.empty_body')}</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4" data-testid="catalog-grid">
                                {products.map((p) => <ProductCard key={p.id} p={p} onOpen={openQuickview} />)}
                            </div>
                        )}

                        {/* Pagination */}
                        {!loadingProducts && total > PAGE_SIZE && (
                            <div className="flex items-center justify-between pt-4" data-testid="catalog-pagination">
                                <span className="text-sm text-gray-500">
                                    {t('catalog.pagination_label', { page, total: totalPages })}
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1 || loadingProducts}
                                        data-testid="catalog-prev-btn"
                                    >
                                        <ChevronLeft size={14} className="mr-1" /> {t('catalog.prev')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage((p) => p + 1)}
                                        disabled={!hasMore || loadingProducts}
                                        data-testid="catalog-next-btn"
                                    >
                                        {t('catalog.next')} <ChevronRight size={14} className="ml-1" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        data-testid="catalog-back-to-top-btn"
                                        className="text-[#C08478] border-[#EFE5E1] hover:bg-[#FBF8F6] hover:text-[#1A1A1A]"
                                    >
                                        <ArrowUp size={14} className="mr-1" /> {t('catalog.back_to_top')}
                                    </Button>
                                </div>
                            </div>
                        )}
                </div>
            </div>
            {/* Quick-view modal — one instance mounted at the page root */}
            <PortalProductQuickView
                sku={quickviewSku}
                open={Boolean(quickviewSku)}
                onOpenChange={(open) => { if (!open) setQuickviewSku(null); }}
            />
        </PortalLayout>
    );
};

export default PortalCatalog;
